import { api } from '@core/api';
import { API_ENDPOINTS } from '@core/config/constants';

interface SendPasswordlessCodeParams {
  email: string;
  restaurant: string;
}

interface SendPasswordlessCodeResponse {
  success: boolean;
  message?: string;
}

interface VerifyPasswordlessCodeParams {
  token: string;
  email?: string;
}

interface VerifyPasswordlessCodeResponse {
  token: string;
  email: string;
  userId?: string;
}

export async function sendPasswordlessCode(
  params: SendPasswordlessCodeParams
): Promise<SendPasswordlessCodeResponse> {
  return api.post<SendPasswordlessCodeResponse>(
    API_ENDPOINTS.PASSWORDLESS_SEND,
    params,
    { skipAuth: true }
  );
}

export async function verifyPasswordlessCode(
  params: VerifyPasswordlessCodeParams
): Promise<VerifyPasswordlessCodeResponse> {
  return api.post<VerifyPasswordlessCodeResponse>(
    API_ENDPOINTS.PASSWORDLESS_VERIFY,
    params,
    { skipAuth: true }
  );
}

interface LoginWithPasswordParams {
  username: string;
  password: string;
}

interface LoginWithPasswordResponse {
  token: string;
}

/**
 * Login with email + password.
 * POST /api/login_check (Symfony/FOSUserBundle JSON login)
 * Payload: { username, password } → { token } (JWT)
 */
export async function loginWithPassword(
  params: LoginWithPasswordParams
): Promise<LoginWithPasswordResponse> {
  return api.post<LoginWithPasswordResponse>(
    '/api/login_check',
    params,
    { skipAuth: true }
  );
}

export interface RegisterAndLoginParams {
  username: string;
  name: string;
  password: string;
  phone: string;
  restaurant: string;
  optInEmail: boolean;
  /** Empty string for email signup; 'google' / 'facebook' only from the auth subdomain. */
  provider?: string;
  is18Year?: boolean;
}

export interface RegisterAndLoginResponse {
  token: string;
  email: string;
  userId?: string;
}

/**
 * Register a new account and receive a session token in the same call.
 * POST /api/{lang}/register-and-login
 *
 * Used by both the cross-subdomain signup page and the in-flow signup step;
 * the backend stores `provider` to remember how the account was created.
 */
export async function registerAndLogin(
  lang: string,
  params: RegisterAndLoginParams,
): Promise<RegisterAndLoginResponse> {
  return api.post<RegisterAndLoginResponse>(
    `/api/${lang}/register-and-login`,
    {
      username: params.username,
      name: params.name,
      password: params.password,
      phone: params.phone,
      restaurant: params.restaurant,
      optInEmail: params.optInEmail,
      provider: params.provider ?? '',
      is18Year: params.is18Year ?? true,
    },
    { skipAuth: true },
  );
}

// --- Password Reset ---

/**
 * Request a password reset email.
 * POST /api/{lang}/reset-password/send-email
 */
export async function sendPasswordResetEmail(
  lang: string,
  params: { username: string; restaurant: string },
): Promise<void> {
  await api.post(`/api/${lang}/reset-password/send-email`, params, { skipAuth: true });
}

/**
 * Validate a password reset token.
 * GET /api/{lang}/reset-password/check/{token}
 */
export async function checkPasswordResetToken(
  lang: string,
  token: string,
): Promise<void> {
  await api.get(`/api/${lang}/reset-password/check/${token}`, { skipAuth: true });
}

/**
 * Set a new password using a valid reset token.
 * POST /api/{lang}/reset-password/set-new-password
 */
export async function setNewPassword(
  lang: string,
  params: { newPass: string; token: string },
): Promise<void> {
  await api.post(`/api/${lang}/reset-password/set-new-password`, params, { skipAuth: true });
}

/**
 * Check if the authenticated user has reached reservation limits for a given date.
 */
export async function checkUserLimitReached(
  restaurantId: number,
  resDate: string,
): Promise<{ dailyReachLimit: boolean; weeklyReachLimit: boolean }> {
  return api.get('/api/get-user-has-reach-limit', {
    params: { restaurantId, resDate },
  });
}

/**
 * Response from GET /get-user-info
 *
 * The backend returns a nested structure where `user` contains the profile
 * data, and `user.user` contains the underlying account (with email).
 */
export interface UserInfoResponse {
  user: {
    full_name: string;
    phone: string;
    optInEmail: boolean;
    is18Year: boolean;
    user: {
      id: number;
      email: string;
      roles: string[];
    };
  };
  isSocialNetworkUser: boolean;
  userRestaurantId: string | number | number[] | null;
  blockedInRestaurantsId: string[];
}

/**
 * Fetch the authenticated user's profile.
 * Requires a valid JWT token in the Authorization header (handled by apiClient).
 */
export async function getUserInfo(
  subdomainPrefix?: string
): Promise<UserInfoResponse> {
  const url = subdomainPrefix
    ? `${API_ENDPOINTS.GET_USER_INFO}?subdomain_prefix=${subdomainPrefix}`
    : API_ENDPOINTS.GET_USER_INFO;

  return api.get<UserInfoResponse>(url);
}
